import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { AppHeader } from '../components/AppHeader.js';
import { SearchBar } from '../components/SearchBar.js';
import { RecentList } from '../components/RecentList.js';
import { LoadingOverlay } from '../components/LoadingOverlay.js';
import { Icon } from '../components/Icon.js';
import { ReviewEntryButton } from '../components/ReviewEntryButton.js';
import { deleteSession, fetchRecentSessions, initContent } from '../lib/api.js';
import { parseQuery } from '../lib/parseQuery.js';
import type { InitStreamLine, ParsedQuery, RecentSession } from '../lib/types.js';

type LoadState = {
  title: string;
  progress: number;
  step: string;
  detail?: string;
  error: { code: string; message: string } | null;
  query: Extract<ParsedQuery, { ok: true }>;
};

export function Home(): JSX.Element {
  const [, setLocation] = useLocation();
  const [recent, setRecent] = useState<RecentSession[]>([]);
  const [validation, setValidation] = useState<string | null>(null);
  const [loading, setLoading] = useState<LoadState | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchRecentSessions(5)
      .then((items) => {
        if (!cancelled) setRecent(items);
      })
      .catch(() => {
        // Silent: home page never blocks on this list.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startInit = (q: Extract<ParsedQuery, { ok: true }>) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading({
      title: q.title,
      progress: 0,
      step: 'resolving_title',
      error: null,
      query: q,
    });
    void initContent(
      q,
      (line: InitStreamLine) => {
        if ('error' in line) {
          setLoading((prev) =>
            prev
              ? {
                  ...prev,
                  error: { code: line.error.code, message: line.error.message },
                }
              : prev,
          );
          return;
        }
        if ('done' in line) {
          setLocation(`/test/${encodeURIComponent(line.content_id)}`);
          return;
        }
        setLoading((prev) =>
          prev
            ? {
                ...prev,
                progress: line.progress,
                step: line.step,
                detail: line.detail,
              }
            : prev,
        );
      },
      ac.signal,
    ).catch(() => {
      setLoading((prev) =>
        prev
          ? {
              ...prev,
              error: { code: 'network', message: '网络中断，请重试' },
            }
          : prev,
      );
    });
  };

  const handleSubmit = (raw: string) => {
    const parsed = parseQuery(raw);
    if (!parsed.ok) {
      setValidation(parsed.error);
      return;
    }
    setValidation(null);
    startInit(parsed);
  };

  const handleHome = () => {
    abortRef.current?.abort();
    setLoading(null);
  };

  if (loading) {
    return (
      <>
        <AppHeader onHome={handleHome} />
        <LoadingOverlay
          title={loading.title}
          progress={loading.progress}
          step={loading.step}
          detail={loading.detail}
          error={loading.error}
          onRetry={loading.error ? () => startInit(loading.query) : undefined}
        />
      </>
    );
  }

  return (
    <>
      <AppHeader />
      <main className="flex-1 flex flex-col items-center px-6">
        <div className="mt-6 text-secondary flex flex-col items-center gap-1">
          <Icon name="film" size={56} />
          <span className="text-muted text-hint">M5</span>
        </div>
        <div className="w-full max-w-card mt-8">
          <SearchBar onSubmit={handleSubmit} />
          {validation ? (
            <div className="text-secondary text-hint mt-2 px-1">{validation}</div>
          ) : null}
        </div>
        <div className="w-full max-w-card">
          <RecentList
            items={recent}
            onPick={(id) => setLocation(`/test/${encodeURIComponent(id)}`)}
            onDelete={(id) => {
              // Optimistic remove. On failure, splice back at the original
              // position so the list order (recency-sorted) is preserved.
              const removedIdx = recent.findIndex((r) => r.content_id === id);
              if (removedIdx === -1) return;
              const removed = recent[removedIdx];
              setRecent((cur) => cur.filter((r) => r.content_id !== id));
              deleteSession(id).catch(() => {
                if (!removed) return;
                setRecent((cur) => {
                  const next = cur.slice();
                  next.splice(removedIdx, 0, removed);
                  return next;
                });
              });
            }}
          />
        </div>
      </main>
      <ReviewEntryButton />
    </>
  );
}
